import type { TxEngineLogger } from '../tx-engine/config.js';
import {
  hashDiagnosticEntity,
  isValidTransactionTraceId,
} from '../tx-engine/diagnostics/lifecycle.js';
import {
  safeRecoveryActionType,
  safeTxErrorCode,
  safeTxStage,
} from '../tx-engine/errors/errors.js';
import { signingFailureFields } from '../tx-engine/submit/lifecycle-telemetry.js';
import type { SubmissionOptions } from '../tx-engine/types.js';
import type { FailedSubmissionResult } from './retry-submission.js';

type SubmissionMetadata = NonNullable<SubmissionOptions['metadata']>;

type MarketCreationLogStage = 'preparing' | 'editorial' | 'shell' | 'outcome';
type MarketCreationInstruction =
  | 'resolve_relay_fee_payer'
  | 'post_market_editorial'
  | 'stage_outcome_metadata'
  | 'create_market'
  | 'prepare_outcomes';

export function errorReason(err: unknown): string {
  return err instanceof Error ? err.message.slice(0, 200) : String(err).slice(0, 200);
}

export function operationalErrorClass(err: unknown): 'Error' | 'non_error' {
  return err instanceof Error ? 'Error' : 'non_error';
}

export function isAbortError(err: unknown): boolean {
  if (err === null || typeof err !== 'object' || !('name' in err)) return false;
  const name = (err as { readonly name?: unknown }).name;
  return name === 'AbortError' || name === 'OrchestratorAbort';
}

export function baseMarketCreationMetadata(args: {
  readonly marketCreationTraceId: string;
  readonly marketPubkey: string;
  readonly creatorPubkey: string;
  readonly marketNonceDecimal: string;
  readonly ticker: string;
  readonly outcomeCount: number;
}): SubmissionMetadata {
  return {
    marketCreationTraceId: args.marketCreationTraceId,
    marketHash: hashDiagnosticEntity(args.marketPubkey),
    creatorHash: hashDiagnosticEntity(args.creatorPubkey),
    marketNonceHash: hashDiagnosticEntity(args.marketNonceDecimal),
    tickerHash: hashDiagnosticEntity(args.ticker),
    outcomeCount: args.outcomeCount,
  };
}

export function stageMetadata(
  base: SubmissionMetadata,
  stage: MarketCreationLogStage,
  instructionName: MarketCreationInstruction,
  extras: SubmissionMetadata = {},
): SubmissionMetadata {
  return {
    ...base,
    marketCreationStage: stage,
    instructionName,
    ...extras,
  };
}

export function submissionFailureLog(
  metadata: SubmissionMetadata,
  result: FailedSubmissionResult,
): Record<string, unknown> {
  return {
    ...metadata,
    ...(isValidTransactionTraceId(result.traceId) ? { txTraceId: result.traceId } : {}),
    errorClass: 'TxError',
    errorCode: safeTxErrorCode(result.error.code),
    errorStage: safeTxStage(result.error.stage),
    recoveryAction: safeRecoveryActionType(result.error.recoveryAction.type),
    ...signingFailureFields(result.error.details ?? {}, result.error.stage),
  };
}

export async function buildWithFailureLog<Result>(input: {
  readonly build: () => Promise<Result>;
  readonly logger: TxEngineLogger | undefined;
  readonly metadata: SubmissionMetadata;
}): Promise<Result> {
  try {
    return await input.build();
  } catch (err) {
    if (isAbortError(err)) throw err;
    input.logger?.error('market_creation.builder_failed', {
      ...input.metadata,
      errorClass: operationalErrorClass(err),
      errorCode: 'MC_BUILDER_FAILED',
      errorStage: 'building',
    });
    throw err;
  }
}
